/* eslint-disable no-param-reassign */
import React, { Component } from 'react';
import { ThemeProvider } from '@peoplecues/commons';
import ReactDOM from 'react-dom';
import analytics from '@xto10x/core/lib/analytics';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import sentry from '@xto10x/core/lib/sentry';
import storage from '@xto10x/storage/lib';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { clone } from '@xto10x/core/lib/app.utils';
import initializePolyfill from 'utils/customPolyfill';
import {
  SentryApiErrorWhitelist,
  SentryWhitelist,
  StorageKey
} from 'utils/constants';
import { AnalyticsCategory } from 'utils/analytics.constants';
import { isNewAppVersionAvailable } from 'containers/App/store/actions';
import { store, history } from './store';
import GlobalStyles from './styles/globalStyle';
import Theme from './styles/theme';
import App from './containers/App/views';
import { isProdEnvironment } from './utils/config';
import './polyfills';
import GlobalTracker from './utils/analyticsGlobalTracker';
import ErrorBoundary from './errorboundary';
import { AnalyticsDefaultOptions } from './utils/constants';

const swUrl = `${process.env.PUBLIC_URL}service-worker.js`;

const isCookieAndStorageAvailable = () =>
  storage.hasCookieAccess() && storage.hasLocalStorageAccess();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      networkMode: 'always'
    }
  }
});

// eslint-disable-next-line react/prefer-stateless-function
class Root extends Component {
  constructor(props) {
    super(props);

    this.state = {
      eventId: null
    };
    initializePolyfill();
    analytics.loadConfig({
      apiErrorWhitelist: SentryApiErrorWhitelist,
      ...AnalyticsDefaultOptions,
      useSecureCookie: isCookieAndStorageAvailable()
    });
    this.checkForServiceWorkerUpdate = this.checkForServiceWorkerUpdate.bind(
      this
    );
    this.broadCastChannel =
      'BroadcastChannel' in window
        ? new BroadcastChannel('people_cues_session_update')
        : null;
    this.handleOrgDetailsUpdate = this.handleOrgDetailsUpdate.bind(this);
    this.handleOnSignOut = this.handleOnSignOut.bind(this);
  }

  componentDidMount() {
    // Removing Initial Loader when app is mounted
    const initialLoader = document.getElementById('initialLoader');
    initialLoader?.remove();

    document.addEventListener(
      'org_details_updated',
      this.handleOrgDetailsUpdate
    );
    window.addEventListener('focus', this.checkForServiceWorkerUpdate);
    if (this.broadCastChannel && !this.broadCastChannel.onmessage) {
      this.broadCastChannel.onmessage = ev => {
        const localOrgInfo = storage.getDataFromLocal(StorageKey.ORG_INFO);
        if (
          ev.isTrusted &&
          ['signout', 'org_details_updated'].indexOf(ev.data) > -1
        ) {
          if (ev.data === 'org_details_updated' && localOrgInfo) {
            storage.saveDataInLocal(StorageKey.IS_ORG_SYNC_NEEDED, true);
            window.location.assign(`/${localOrgInfo.sub_domain}/dashboard`);
          } else {
            window.location.reload();
          }
        }
      };
    }
  }

  componentDidCatch(error, errorInfo) {
    sentry.withScope(scope => {
      scope.setExtras(errorInfo);
      const eventId = sentry.captureException(error);
      this.setState({ eventId });
    });
  }

  componentWillUnmount() {
    document.removeEventListener(
      'org_details_updated',
      this.handleOrgDetailsUpdate
    );
    window.removeEventListener('focus', this.checkForServiceWorkerUpdate);
    if (this.broadCastChannel) this.broadCastChannel.close();
  }

  handleOrgDetailsUpdate() {
    if (this.broadCastChannel)
      this.broadCastChannel.postMessage('org_details_updated');
  }

  handleOnSignOut() {
    if (this.broadCastChannel) this.broadCastChannel.postMessage('signout');
  }

  reportError = () => {
    if (this.eventId == null || isProdEnvironment) return;
    const eventId = clone(this.state.eventId);
    sentry.showReportDialog({ eventId });
    this.setState({ eventId: null });
  };

  getServiceWorkerRegistration = () =>
    navigator.serviceWorker.getRegistration();

  checkForServiceWorkerUpdate() {
    if ('serviceWorker' in navigator)
      this.getServiceWorkerRegistration()
        .then(registration => registration?.update())
        .then(updatedRegistration => {
          if (updatedRegistration.active && updatedRegistration.installing) {
            if (store.getState()?.appReducer?.isAuthenticated)
              store.dispatch(isNewAppVersionAvailable(true));
            else {
              analytics.trackEvent(
                'manual_page_reload',
                'service_worker_updated'
              );
              window.location.reload();
            }
          }
        })
        .catch(() => null);
  }

  render() {
    return (
      <Provider store={store}>
        <GlobalTracker />
        <ConnectedRouter history={history}>
          <ThemeProvider theme={Theme}>
            <QueryClientProvider client={queryClient}>
              <GlobalStyles />
              <ErrorBoundary>
                <App handleOnSignOut={this.handleOnSignOut} />
              </ErrorBoundary>
              <ReactQueryDevtools initialIsOpen={false} />
            </QueryClientProvider>
          </ThemeProvider>
        </ConnectedRouter>
      </Provider>
    );
  }
}

// Initialize Sentry
sentry.initialize(SentryWhitelist);

ReactDOM.render(<Root />, document.getElementById('root'));

if ('serviceWorker' in navigator) {
  // eslint-disable-next-line no-unused-expressions
  navigator.serviceWorker.register(swUrl)?.then(
    () => {
      analytics.trackEvent(
        AnalyticsCategory.service_worker,
        'Service worker registration succeeded'
      );
    },
    /* catch */ err => {
      analytics.trackError(AnalyticsCategory.service_worker, err);
    }
  );
} else {
  analytics.trackError(
    AnalyticsCategory.service_worker,
    'Service workers are not supported'
  );
}
